// ============================================
// Zustand Store
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Child, PersonaId, Message, Admin } from '../types';
import { authAPI, childrenAPI } from '../services/api';

// ============================================
// Auth Store
// ============================================

interface AuthStore {
  // User auth
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;

  // Admin auth
  admin: Admin | null;
  adminToken: string | null;

  // State
  isLoading: boolean;
  error: string | null;
  
  // Hydration state
  hasHydrated: boolean;
  setHasHydrated: (value: boolean) => void;

  // User actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string) => Promise<boolean>;
  setAuth: (user: User, token: string) => void;
  logout: () => void;

  // Admin actions
  adminLogin: (email: string, password: string) => Promise<boolean>;
  adminLogout: () => void;

  // Utils
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      admin: null,
      adminToken: null,
      isLoading: false,
      error: null,
      hasHydrated: false,
      setHasHydrated: (value) => set({ hasHydrated: value }),

      // User login
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login(email, password);
          if (response.token && response.user) {
            set({
              user: response.user,
              token: response.token,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }
          set({ error: 'Login failed', isLoading: false });
          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false,
          });
          return false;
        }
      },

      // User register
      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.register(email, password, name);
          if (response.token && response.user) {
            set({
              user: response.user,
              token: response.token,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }
          set({ error: 'Registration failed', isLoading: false });
          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false,
          });
          return false;
        }
      },

      setAuth: (user, token) => set({ user, token, isAuthenticated: true }),

      logout: () => set({
        user: null,
        token: null,
        isAuthenticated: false,
        error: null,
      }),

      // Admin login
      adminLogin: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.adminLogin(email, password);
          // 后端返回 user 字段，需要映射为 admin
          if (response.token && response.user) {
            // 映射后端数据到 Admin 类型
            const admin = {
              id: response.user.id,
              email: response.user.email,
              name: response.user.email.split('@')[0], // 从邮箱生成名称
              role: 'admin' as const, // 统一映射为 'admin'
            };
            set({
              admin,
              adminToken: response.token,
              isLoading: false,
            });
            return true;
          }
          set({ error: 'Admin login failed', isLoading: false });
          return false;
        } catch (error: any) {
          set({
            error: error.message || 'Admin login failed',
            isLoading: false,
          });
          return false;
        }
      },

      adminLogout: () => set({
        admin: null,
        adminToken: null,
        error: null,
      }),

      clearError: () => set({ error: null }),
    }),
    {
      name: 'equantu-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        admin: state.admin,
        adminToken: state.adminToken,
      }),
      // 确保 adminToken 被正确恢复
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
          console.log('🔄 Store rehydrated:', { admin: !!state.admin, adminToken: !!state.adminToken });
        }
      },
    }
  )
);

// ============================================
// Children Store
// ============================================

interface ChildrenStore {
  children: Child[];
  currentChild: Child | null;
  isLoading: boolean;
  error: string | null;

  setChildren: (children: Child[]) => void;
  setCurrentChild: (child: Child | null) => void;
  addChild: (child: Child) => void;
  updateChild: (id: string, updates: Partial<Child>) => void;
  removeChild: (id: string) => void;
  fetchChildren: () => Promise<void>;
}

export const useChildrenStore = create<ChildrenStore>((set, get) => ({
  children: [],
  currentChild: null,
  isLoading: false,
  error: null,

  setChildren: (children) => set({ children }),
  setCurrentChild: (child) => set({ currentChild: child }),
  addChild: (child) => set((state) => ({ children: [...state.children, child] })),
  updateChild: (id, updates) =>
    set((state) => ({
      children: state.children.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      currentChild: state.currentChild?.id === id ? { ...state.currentChild, ...updates } : state.currentChild,
    })),
  removeChild: (id) =>
    set((state) => ({
      children: state.children.filter((c) => c.id !== id),
      currentChild: state.currentChild?.id === id ? null : state.currentChild,
    })),

  fetchChildren: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await childrenAPI.list();
      set({
        children: response.children || [],
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch children',
        isLoading: false,
      });
    }
  },
}));

// ============================================
// Chat Store
// ============================================

interface ChatStore {
  currentPersona: PersonaId | null;
  sessionId: string | null;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  setPersona: (persona: PersonaId | null) => void;
  setSessionId: (sessionId: string | null) => void;
  addMessage: (message: Message) => void;
  setMessages: (messages: Message[]) => void;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetChat: () => void;
}

export const useChatStore = create<ChatStore>((set) => ({
  currentPersona: null,
  sessionId: null,
  messages: [],
  isLoading: false,
  error: null,
  setPersona: (persona) => set({ currentPersona: persona }),
  setSessionId: (sessionId) => set({ sessionId }),
  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
  setMessages: (messages) => set({ messages }),
  clearMessages: () => set({ messages: [] }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  resetChat: () => set({ currentPersona: null, sessionId: null, messages: [], error: null }),
}));

// ============================================
// UI Store
// ============================================

interface UIStore {
  isSidebarOpen: boolean;
  isVoiceMode: boolean;
  isRecording: boolean;
  isPlaying: boolean;
  toggleSidebar: () => void;
  setVoiceMode: (enabled: boolean) => void;
  setRecording: (recording: boolean) => void;
  setPlaying: (playing: boolean) => void;
}

export const useUIStore = create<UIStore>((set) => ({
  isSidebarOpen: false,
  isVoiceMode: false,
  isRecording: false,
  isPlaying: false,
  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
  setVoiceMode: (enabled) => set({ isVoiceMode: enabled }),
  setRecording: (recording) => set({ isRecording: recording }),
  setPlaying: (playing) => set({ isPlaying: playing }),
}));
